import { rendererCapacity } from "./renderer-capacity";

type Phase12Packet = {
  nodes: number[];
  connections: number[];
  inputIndices: number[];
  outputIndices: number[];
};

type Phase12Timing = {
  supported: boolean;
  backendMs: number;
  uploadMs: number;
  drawMs: number;
  framesPerSecond: number;
  note: string | null;
};

type Phase12Lifecycle = {
  supported: boolean;
  pipelineCreates: number;
  note: string | null;
};

type Phase12WebGlRenderer = {
  renderGallery(canvasId: string, statusId: string, packets: Phase12Packet[], compileMs?: number, qualityScale?: number): { totalMs: number; packMs: number; uploadMs: number; drawMs: number } | null;
};

declare global {
  interface Window {
    patternEvolverRenderer: Phase12WebGlRenderer;
    phase12WebGpuExperiment: {
      renderGeneration(backend: string, packets: Phase12Packet[]): Promise<Phase12Timing>;
      runSustained(backend: string, packet: Phase12Packet, frameCount: number): Promise<Phase12Timing>;
      checkParity(packet: Phase12Packet): Promise<{ match: boolean; webGlPixel: number[]; webGpuPixel: number[] }>;
      getLifecycle(): Phase12Lifecycle;
    };
  }
}

const { maxNodes, maxConnections } = rendererCapacity;
const maxCandidates = 13;
const copyDst = 8;
const uniformUsage = 64;
const storageUsage = 128;
const copySrcTextureUsage = 1;
const renderAttachmentTextureUsage = 16;

const shaderSource = `
struct Params {
  tileOrigin: vec2<f32>,
  tileSize: vec2<f32>,
  sampleSize: vec2<f32>,
  counts: vec2<u32>,
  flags: vec2<u32>,
};

@group(0) @binding(0) var<storage, read> nodes: array<vec4<f32>>;
@group(0) @binding(1) var<storage, read> connections: array<vec4<f32>>;
@group(0) @binding(2) var<uniform> params: Params;
@group(0) @binding(3) var<storage, read> indices: array<i32>;

fn modPositive(value: f32, divisor: f32) -> f32 {
  return value - divisor * floor(value / divisor);
}

fn rescale(value: f32) -> f32 { return 2.0 * value - 1.0; }

fn activate(kind: i32, value: f32) -> f32 {
  if (kind == 0) { return value; }
  if (kind == 1) { return sin(3.14159265 * value); }
  if (kind == 2) { return 2.0 * abs(modPositive(value, 2.0) - 1.0) - 1.0; }
  if (kind == 3) { return modPositive(value - 1.0, 2.0) - 1.0; }
  if (kind == 4) { return select(-1.0, 1.0, modPositive(value - 1.0, 2.0) - 1.0 > 0.0); }
  if (kind == 5) { return rescale(1.0 / (1.0 + exp(-value))); }
  if (kind == 6) { return rescale(exp(-pow(2.5 * value, 2.0))); }
  return 2.0 * value * exp(0.5 - 2.0 * value * value);
}

@vertex
fn vertexMain(@builtin(vertex_index) vertexIndex: u32) -> @builtin(position) vec4<f32> {
  var positions = array<vec2<f32>, 3>(vec2<f32>(-1.0, -1.0), vec2<f32>(3.0, -1.0), vec2<f32>(-1.0, 3.0));
  return vec4<f32>(positions[vertexIndex], 0.0, 1.0);
}

@fragment
fn fragmentMain(@builtin(position) position: vec4<f32>) -> @location(0) vec4<f32> {
  var tileUv = (position.xy - params.tileOrigin - vec2<f32>(0.5)) / params.tileSize;
  tileUv = (floor(tileUv * params.sampleSize) + vec2<f32>(0.5)) / params.sampleSize;
  var xy = tileUv * 2.0 - 1.0;
  var inputs: array<f32, 6>;
  inputs[0] = xy.x;
  inputs[1] = xy.y;
  inputs[2] = length(xy) / 1.41421356;
  inputs[3] = 0.0;
  inputs[4] = 0.0;
  inputs[5] = 1.0;

  var values: array<f32, ${maxNodes}>;
  for (var nodeIndex: u32 = 0u; nodeIndex < ${maxNodes}u; nodeIndex = nodeIndex + 1u) {
    if (nodeIndex >= params.counts.x) { break; }
    var isInput = false;
    var inputValue = 0.0;
    for (var slot: u32 = 0u; slot < 6u; slot = slot + 1u) {
      if (indices[slot] == i32(nodeIndex)) {
        isInput = true;
        inputValue = inputs[slot];
      }
    }
    if (isInput) {
      values[nodeIndex] = inputValue;
      continue;
    }

    let node = nodes[nodeIndex];
    var sum = node.y;
    let first = u32(node.z);
    let count = u32(node.w);
    for (var offset: u32 = 0u; offset < ${maxConnections}u; offset = offset + 1u) {
      if (offset >= count || first + offset >= params.counts.y) { break; }
      let connection = connections[first + offset];
      sum = sum + values[u32(connection.x)] * connection.y;
    }
    values[nodeIndex] = activate(i32(node.x), sum);
  }

  let color = vec3<f32>(
    values[u32(indices[6])] * 0.5 + 0.5,
    values[u32(indices[7])] * 0.5 + 0.5,
    values[u32(indices[8])] * 0.5 + 0.5
  );
  return vec4<f32>(color, 1.0);
}`;

type WebGpuState = {
  canvas: HTMLCanvasElement;
  device: any;
  context: any;
  pipeline: any;
  bindGroups: any[];
  nodeBuffers: any[];
  connectionBuffers: any[];
  uniformBuffers: any[];
  indexBuffers: any[];
  nodeStaging: Float32Array[];
  connectionStaging: Float32Array[];
  indexStaging: Int32Array[];
  uniformBuffersData: ArrayBuffer[];
  uniformFloats: Float32Array[];
  uniformIntegers: Uint32Array[];
  uniformBytes: Uint8Array[];
  readback: any;
  format: string;
};

let webGpuState: WebGpuState | null = null;
let webGpuPipelineCreates = 0;
let webGpuUnavailableNote: string | null = null;

async function getWebGpuState(canvasId: string): Promise<WebGpuState | null> {
  if (webGpuState) return webGpuState;
  const gpu = (navigator as Navigator & { gpu?: any }).gpu;
  if (!gpu) {
    webGpuUnavailableNote = "WebGPU is not exposed by this browser.";
    return null;
  }
  const adapter = await gpu.requestAdapter({ powerPreference: "high-performance" });
  if (!adapter) {
    webGpuUnavailableNote = "No WebGPU adapter is available.";
    return null;
  }
  const device = await adapter.requestDevice();
  const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
  if (!canvas) throw new Error("Phase 12 WebGPU canvas is missing.");
  const context = canvas.getContext("webgpu");
  if (!context) {
    webGpuUnavailableNote = "The browser could not create a WebGPU canvas context.";
    return null;
  }
  const format = gpu.getPreferredCanvasFormat();
  context.configure({ device, format, alphaMode: "opaque", usage: copySrcTextureUsage | renderAttachmentTextureUsage });
  const module = device.createShaderModule({ code: shaderSource });
  const pipeline = device.createRenderPipeline({
    layout: "auto",
    vertex: { module, entryPoint: "vertexMain" },
    fragment: { module, entryPoint: "fragmentMain", targets: [{ format }] },
    primitive: { topology: "triangle-list" }
  });
  webGpuPipelineCreates++;

  const nodeBuffers = Array.from({ length: maxCandidates }, () => device.createBuffer({ size: maxNodes * 16, usage: copyDst | storageUsage }));
  const connectionBuffers = Array.from({ length: maxCandidates }, () => device.createBuffer({ size: maxConnections * 16, usage: copyDst | storageUsage }));
  const uniformBuffers = Array.from({ length: maxCandidates }, () => device.createBuffer({ size: 48, usage: copyDst | uniformUsage }));
  const indexBuffers = Array.from({ length: maxCandidates }, () => device.createBuffer({ size: 9 * 4, usage: copyDst | storageUsage }));
  const bindGroupLayout = pipeline.getBindGroupLayout(0);
  const bindGroups = Array.from({ length: maxCandidates }, (_, index) => device.createBindGroup({
    layout: bindGroupLayout,
    entries: [
      { binding: 0, resource: { buffer: nodeBuffers[index] } },
      { binding: 1, resource: { buffer: connectionBuffers[index] } },
      { binding: 2, resource: { buffer: uniformBuffers[index] } },
      { binding: 3, resource: { buffer: indexBuffers[index] } }
    ]
  }));

  webGpuState = {
    canvas,
    device,
    context,
    pipeline,
    bindGroups,
    nodeBuffers,
    connectionBuffers,
    uniformBuffers,
    indexBuffers,
    nodeStaging: Array.from({ length: maxCandidates }, () => new Float32Array(maxNodes * 4)),
    connectionStaging: Array.from({ length: maxCandidates }, () => new Float32Array(maxConnections * 4)),
    indexStaging: Array.from({ length: maxCandidates }, () => new Int32Array(9)),
    uniformBuffersData: Array.from({ length: maxCandidates }, () => new ArrayBuffer(48)),
    uniformFloats: [],
    uniformIntegers: [],
    uniformBytes: [],
    readback: device.createBuffer({ size: 256, usage: copyDst | 1 }),
    format
  };
  webGpuState.uniformFloats = webGpuState.uniformBuffersData.map(buffer => new Float32Array(buffer));
  webGpuState.uniformIntegers = webGpuState.uniformBuffersData.map(buffer => new Uint32Array(buffer));
  webGpuState.uniformBytes = webGpuState.uniformBuffersData.map(buffer => new Uint8Array(buffer));
  device.lost.then(() => {
    if (webGpuState?.device === device) webGpuState = null;
  });
  return webGpuState;
}

function writePacket(state: WebGpuState, index: number, packet: Phase12Packet, x: number, y: number, width: number, height: number, sampleWidth: number, sampleHeight: number): number {
  const nodes = state.nodeStaging[index];
  const connections = state.connectionStaging[index];
  const indices = state.indexStaging[index];
  nodes.set(packet.nodes);
  connections.set(packet.connections);
  indices.fill(0);
  indices.set(packet.inputIndices, 0);
  indices.set(packet.outputIndices, 6);
  const floats = state.uniformFloats[index];
  const integers = state.uniformIntegers[index];
  floats[0] = x; floats[1] = y;
  floats[2] = width; floats[3] = height;
  floats[4] = sampleWidth; floats[5] = sampleHeight;
  integers[6] = packet.nodes.length / 4;
  integers[7] = packet.connections.length / 4;
  integers[8] = 0;
  integers[9] = 0;
  const started = performance.now();
  state.device.queue.writeBuffer(state.nodeBuffers[index], 0, nodes.subarray(0, packet.nodes.length));
  if (packet.connections.length > 0) state.device.queue.writeBuffer(state.connectionBuffers[index], 0, connections.subarray(0, packet.connections.length));
  state.device.queue.writeBuffer(state.indexBuffers[index], 0, indices);
  state.device.queue.writeBuffer(state.uniformBuffers[index], 0, state.uniformBytes[index]);
  return performance.now() - started;
}

async function renderWebGpuGallery(packets: Phase12Packet[], canvasId = "phase12-webgpu-canvas"): Promise<Phase12Timing> {
  const state = await getWebGpuState(canvasId);
  if (!state) return { supported: false, backendMs: 0, uploadMs: 0, drawMs: 0, framesPerSecond: 0, note: webGpuUnavailableNote };
  if (packets.length > maxCandidates) return { supported: false, backendMs: 0, uploadMs: 0, drawMs: 0, framesPerSecond: 0, note: "The Phase 12 harness supports at most 13 candidates." };

  const columns = Math.ceil(Math.sqrt(packets.length));
  const rows = Math.ceil(packets.length / columns);
  const tileWidth = Math.floor(state.canvas.width / columns);
  const tileHeight = Math.floor(state.canvas.height / rows);
  const renderWidth = tileWidth;
  const renderHeight = tileHeight;
  const started = performance.now();
  let uploadMs = 0;
  const encoder = state.device.createCommandEncoder();
  const pass = encoder.beginRenderPass({
    colorAttachments: [{ view: state.context.getCurrentTexture().createView(), loadOp: "clear", storeOp: "store", clearValue: { r: 0, g: 0, b: 0, a: 1 } }]
  });
  pass.setPipeline(state.pipeline);
  for (let index = 0; index < packets.length; index++) {
    const column = index % columns;
    const row = Math.floor(index / columns);
    uploadMs += writePacket(state, index, packets[index], column * tileWidth, row * tileHeight, tileWidth, tileHeight, renderWidth, renderHeight);
    pass.setBindGroup(0, state.bindGroups[index]);
    pass.draw(3);
  }
  pass.end();
  state.device.queue.submit([encoder.finish()]);
  await state.device.queue.onSubmittedWorkDone();
  const backendMs = performance.now() - started;
  return { supported: true, backendMs, uploadMs, drawMs: Math.max(0, backendMs - uploadMs), framesPerSecond: 0, note: null };
}

async function renderWebGpuParity(packet: Phase12Packet): Promise<number[]> {
  const state = await getWebGpuState("phase12-webgpu-canvas");
  if (!state) return [];
  const texture = state.context.getCurrentTexture();
  const encoder = state.device.createCommandEncoder();
  const uploadMs = writePacket(state, 0, packet, 0, 0, state.canvas.width, state.canvas.height, state.canvas.width, state.canvas.height);
  void uploadMs;
  const pass = encoder.beginRenderPass({
    colorAttachments: [{ view: texture.createView(), loadOp: "clear", storeOp: "store", clearValue: { r: 0, g: 0, b: 0, a: 1 } }]
  });
  pass.setPipeline(state.pipeline);
  pass.setBindGroup(0, state.bindGroups[0]);
  pass.draw(3);
  pass.end();
  encoder.copyTextureToBuffer({ texture, origin: { x: Math.floor(state.canvas.width / 2), y: Math.floor(state.canvas.height / 2), z: 0 } }, { buffer: state.readback, bytesPerRow: 256 }, { width: 1, height: 1, depthOrArrayLayers: 1 });
  state.device.queue.submit([encoder.finish()]);
  await state.device.queue.onSubmittedWorkDone();
  await state.readback.mapAsync(1);
  const raw = Array.from(new Uint8Array(state.readback.getMappedRange()).slice(0, 4));
  state.readback.unmap();
  return state.format === "bgra8unorm" ? [raw[2], raw[1], raw[0], raw[3]] : raw;
}

async function renderGeneration(backend: string, packets: Phase12Packet[]): Promise<Phase12Timing> {
  if (backend === "WebGL2") {
    const started = performance.now();
    const metrics = window.patternEvolverRenderer.renderGallery("phase12-webgl-canvas", "phase12-renderer-status", packets, 0, 1);
    const backendMs = performance.now() - started;
    return metrics
      ? { supported: true, backendMs, uploadMs: metrics.packMs + metrics.uploadMs, drawMs: metrics.drawMs, framesPerSecond: 0, note: null }
      : { supported: false, backendMs, uploadMs: 0, drawMs: 0, framesPerSecond: 0, note: "The existing WebGL2 renderer did not return metrics." };
  }
  return renderWebGpuGallery(packets);
}

async function runSustained(backend: string, packet: Phase12Packet, frameCount: number): Promise<Phase12Timing> {
  const started = performance.now();
  let uploadMs = 0;
  let drawMs = 0;
  let supported = true;
  let note: string | null = null;
  for (let frame = 0; frame < frameCount; frame++) {
    const timing = await renderGeneration(backend, [packet]);
    supported = supported && timing.supported;
    note = timing.note;
    uploadMs += timing.uploadMs;
    drawMs += timing.drawMs;
  }
  const backendMs = performance.now() - started;
  return { supported, backendMs, uploadMs, drawMs, framesPerSecond: supported ? frameCount / (backendMs / 1000) : 0, note };
}

async function checkParity(packet: Phase12Packet): Promise<{ match: boolean; webGlPixel: number[]; webGpuPixel: number[] }> {
  const webGlCanvas = document.getElementById("phase12-webgl-canvas") as HTMLCanvasElement | null;
  const gl = webGlCanvas?.getContext("webgl2", { antialias: false });
  let webGlPixel: number[] = [];
  if (gl) {
    window.patternEvolverRenderer.renderGallery("phase12-webgl-canvas", "phase12-renderer-status", [packet], 0, 1);
    gl.finish();
    const pixel = new Uint8Array(4);
    gl.readPixels(Math.floor((webGlCanvas?.width ?? 640) / 2), Math.floor((webGlCanvas?.height ?? 640) / 2), 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
    webGlPixel = Array.from(pixel);
  }
  const webGpuPixel = await renderWebGpuParity(packet);
  const match = webGlPixel.length === 4 && webGpuPixel.length === 4 && webGlPixel.every((value, index) => Math.abs(value - webGpuPixel[index]) <= 3);
  return { match, webGlPixel, webGpuPixel };
}

function getLifecycle(): Phase12Lifecycle {
  return { supported: webGpuState !== null || webGpuUnavailableNote === null, pipelineCreates: webGpuPipelineCreates, note: webGpuUnavailableNote };
}

window.phase12WebGpuExperiment = { renderGeneration, runSustained, checkParity, getLifecycle };
